import { z } from 'zod';
import { ChipModelDtoSchema } from '@/schemas/chip';
import { ChipIdSchema } from '@/schemas/common';
import { chipFootprint, chipLibraryProblems } from './chips';
import { TileType } from '@/models/TileTypes';
import { CircuitLabelsSchema } from '@/models/CircuitLabelsModel';
import { ScoringRuleSchema } from './scoring/rules';

export const challengeTiles = [
  TileType.Wire,
  TileType.Not,
  TileType.Diode,
  TileType.Delay,
  TileType.SingleInterface,
] as const;
export const tileNames: Record<string, string> = {
  Wire: 'Wire',
  Not: 'NOT',
  Diode: 'Diode',
  Delay: 'Delay',
  SingleInterface: 'Single Interface',
};
// Exhaustive tables and their transition checks grow exponentially. Port counts
// are independent of this legacy bound; larger layouts use selective cases or sequences.
// Keep legacy import capacity independent of the authoring generation threshold.
export const MAX_TRUTH_TABLE_INPUTS = 6;
export const AUTO_TRUTH_TABLE_INPUTS = 5;
export const MAX_TRUTH_TABLE_ROWS = 64;
export const MAX_SCENARIOS = 100;
const bitVector = z.array(z.boolean()).min(1);
const truthVector = z.object({
  inputs: bitVector,
  outputs: z.array(z.boolean().nullable()).min(1),
});
const truthTable = z.object({
  kind: z.literal('truth-table'),
  version: z.literal(2).optional(),
  rows: z.array(truthVector).min(1).max(MAX_TRUTH_TABLE_ROWS),
});
const step = truthVector.extend({
  atMs: z.number().int().min(0).max(60_000).optional(),
  waitMs: z.number().int().min(0).max(60_000).optional(),
});
const name = z.string().trim().min(1).max(40);
export const fixedTileTypes = [
  TileType.Wire,
  TileType.Not,
  TileType.Diode,
  TileType.Delay,
] as const;
const coordinate = z.number().int().min(-16000).max(16000);
export const TerminalPlacementSchema = z.object({
  x: coordinate,
  y: coordinate,
  rotation: z.number().int().min(0).max(3).default(0),
  tint: z
    .string()
    .regex(/^#[0-9a-f]{6}$/i)
    .optional(),
});
const starterConnection = z.object({
  a: z.object({ x: coordinate, y: coordinate }),
  b: z.object({ x: coordinate, y: coordinate }),
});
export const FixedTileSchema = z.object({
  type: z.enum(fixedTileTypes),
  x: coordinate,
  y: coordinate,
  rotation: z.number().int().min(0).max(3).default(0),
  tint: z
    .string()
    .regex(/^#[0-9a-f]{6}$/i)
    .optional(),
  delayMs: z.number().min(1).max(10000).optional(),
});
const footprintDimensions = {
  width: z.number().int().min(1).max(100),
  height: z.number().int().min(1).max(100),
};
const footprint = z.union([
  z.object({ ...footprintDimensions, maxArea: z.undefined().optional() }),
  z.object({
    maxArea: z.number().int().min(1).max(10000),
    // Retain the author's dimension settings when switching modes. They are
    // inactive in area mode; area-only imports need no width or height.
    width: footprintDimensions.width.default(100),
    height: footprintDimensions.height.default(100),
  }),
]);
export const ChallengeSchema = z
  .object({
    id: z.string().min(1).max(100),
    version: z.number().int().positive(),
    title: z.string().trim().min(1).max(100),
    goal: z.string().trim().min(1).max(1000),
    source: z
      .object({
        title: z.string().min(1).max(200),
        url: z
          .url()
          .refine(
            (url) => /^https?:\/\//i.test(url),
            'Use an HTTP or HTTPS source link.',
          ),
      })
      .optional(),
    showSignalPairs: z.boolean().optional(),
    inputs: z.array(name).min(1),
    outputs: z.array(name).min(1),
    inputPositions: z.array(TerminalPlacementSchema.nullable()).min(1).optional(),
    outputPositions: z.array(TerminalPlacementSchema.nullable()).min(1).optional(),
    starterConnections: z.array(starterConnection).max(1024).optional(),
    starterDisconnected: z.array(starterConnection).max(1024).optional(),
    starterLabels: CircuitLabelsSchema.optional(),
    initialInputs: bitVector.optional(),
    buildOrigin: z.object({ x: coordinate, y: coordinate }).optional(),
    // Retain old opt-outs while reading stored files/attempts. Solver editing
    // always locks named terminals; new saves/exports explicitly set true.
    fixedTerminals: z.boolean().optional(),
    requireFullPaths: z.boolean().optional(),
    fixedTiles: z.array(FixedTileSchema).max(256).optional(),
    chipLibrary: z
      .array(
        z.object({
          chip: ChipModelDtoSchema,
          available: z.boolean(),
          max: z.number().int().min(0).max(4096).optional(),
        }),
      )
      .max(256)
      .optional(),
    starterChips: z
      .array(TerminalPlacementSchema.extend({ chipId: ChipIdSchema, fixed: z.boolean() }))
      .max(256)
      .optional(),
    tests: z.discriminatedUnion('kind', [
      truthTable,
      z.object({
        kind: z.literal('sequence'),
        version: z.union([z.literal(2), z.literal(3)]).optional(),
        advance: z.enum(['settled', 'duration']).optional(),
        scenarios: z
          .array(z.object({ name, steps: z.array(step).min(1).max(100) }))
          .min(1)
          .max(MAX_SCENARIOS),
      }),
    ]),
    limits: footprint.and(
      z.object({
        tiles: z
          .array(
            z.object({
              type: z.enum(challengeTiles),
              available: z.boolean().optional(),
              max: z.number().int().min(0).max(4096).optional(),
            }),
          )
          .max(challengeTiles.length),
        feedback: z.boolean(),
      }),
    ),
    optimize: z.object({ wires: z.boolean(), area: z.boolean() }),
    scoring: ScoringRuleSchema.optional(),
    countingVersion: z.union([z.literal(2), z.literal(3)]).optional(),
    playerChips: z
      .object({
        permissionsVersion: z.literal(2).optional(),
        chips: z.boolean(),
        bundleAdapters: z.boolean(),
        create: z.boolean(),
        import: z.boolean(),
        maxDefinitions: z.preprocess(
          (value) => (value === null || value === '' ? undefined : value),
          z.number().int().min(0).max(256).optional(),
        ),
      })
      .strict()
      .optional(),
  })
  .superRefine((c, ctx) => {
    const issue = (message: string, field?: string | string[]) =>
      ctx.addIssue({
        code: 'custom',
        message,
        path: Array.isArray(field) ? field : field ? [field] : [],
      });
    const playerOnly = (c.countingVersion ?? c.scoring?.metricsVersion ?? 1) >= 2;
    if ((c.countingVersion === 3) !== !!c.playerChips)
      issue('Player chip settings require accounting version 3.', 'playerChips');
    if (c.countingVersion === 3 && c.scoring && c.scoring.metricsVersion !== 3)
      issue('Player chips require scoring metrics version 3.', 'scoring');
    if (c.scoring?.metricsVersion === 3 && c.countingVersion !== 3)
      issue('Scoring metrics version 3 requires player chip accounting.', 'scoring');
    if (c.countingVersion === 2 && c.scoring?.metricsVersion === 1)
      issue('Player-only counts require scoring metrics version 2.', 'scoring');
    if (c.limits.maxArea !== undefined && (c.buildOrigin || c.requireFullPaths))
      issue(
        'Maximum area cannot be combined with a positioned build area or full-board paths.',
        'limits',
      );
    if (c.requireFullPaths) {
      if (
        !c.buildOrigin ||
        !c.fixedTerminals ||
        c.inputs.length !== c.outputs.length ||
        hasPlaceableInterfaces(c)
      )
        issue(
          'Full-board paths need a build area and equally many fixed inputs and outputs.',
          'limits',
        );
      if (
        c.chipLibrary?.length ||
        c.fixedTiles?.some((tile) => tile.type !== TileType.Wire) ||
        c.limits.tiles.some(
          (r) =>
            r.available !== false &&
            r.type !== TileType.Wire &&
            r.type !== TileType.SingleInterface,
        )
      )
        issue(
          'Full-board paths use only Wire and Single Interface tiles, without chips.',
          'limits',
        );
      const cells = c.limits.width * c.limits.height;
      const wireRule = c.limits.tiles.find((r) => r.type === TileType.Wire);
      if (
        cells > 4096 ||
        (wireRule && wireRule.available !== false
          ? (wireRule.max ?? Infinity) +
            (playerOnly
              ? (c.fixedTiles?.filter((tile) => tile.type === TileType.Wire).length ?? 0)
              : 0)
          : (c.fixedTiles?.filter((tile) => tile.type === TileType.Wire).length ?? 0)) <
          cells - c.inputs.length - c.outputs.length
      )
        issue(
          'Allow enough Wire tiles to fill the build area (up to 4,096 cells).',
          'limits',
        );
    }
    for (const message of chipLibraryProblems(c.chipLibrary ?? [], !!c.playerChips))
      issue(message, 'chipLibrary');
    for (const p of c.starterChips ?? []) {
      const rule = c.chipLibrary?.find((r) => r.chip.id === p.chipId);
      if (!rule) issue('Include every starting chip in the library.', 'chipLibrary');
      if (!p.fixed && !rule?.available)
        issue('A movable starting chip must be available to players.', 'chipLibrary');
    }
    for (const rule of c.chipLibrary ?? []) {
      const count =
        c.starterChips?.filter((p) => p.chipId === rule.chip.id && !p.fixed).length ?? 0;
      if (rule.max !== undefined && count > rule.max)
        issue(
          `${rule.chip.name}: the starting board exceeds its player allowance.`,
          'chipLibrary',
        );
      if (
        (rule.available || c.starterChips?.some((p) => p.chipId === rule.chip.id)) &&
        !rule.chip.structureCircuitDto.tileManagerDto.tiles.length
      )
        issue(
          `${rule.chip.name}: add a Structure before using this chip.`,
          'chipLibrary',
        );
    }
    const chipCells = (c.starterChips ?? []).flatMap((p) => {
      const chip = c.chipLibrary?.find((r) => r.chip.id === p.chipId)?.chip;
      return chip
        ? chipFootprint(chip, p).map((cell) => ({
            ...cell,
            type: 'chip',
            fixed: p.fixed,
          }))
        : [];
    });
    const starter = [
      ...chipCells,
      ...(['inputs', 'outputs'] as const).flatMap((kind) =>
        c[kind].flatMap((_, i) => {
          const position = terminalPosition(c, kind, i);
          return position ? [{ type: TileType.SingleInterface, ...position }] : [];
        }),
      ),
      ...(c.fixedTiles ?? []),
    ];
    if (c.limits.maxArea !== undefined && !playerOnly) {
      const requiredCells = [
        ...chipCells.filter((cell) => cell.fixed),
        ...(c.fixedTerminals
          ? starter.filter((cell) => cell.type === TileType.SingleInterface)
          : []),
        ...(c.fixedTiles ?? []),
      ];
      if (requiredCells.length) {
        let left = Infinity,
          right = -Infinity,
          top = Infinity,
          bottom = -Infinity;
        for (const cell of requiredCells) {
          left = Math.min(left, cell.x);
          right = Math.max(right, cell.x);
          top = Math.min(top, cell.y);
          bottom = Math.max(bottom, cell.y);
        }
        const area = (right - left + 1) * (bottom - top + 1);
        if (area > c.limits.maxArea)
          issue(
            `Fixed starting parts need bounding area ${area}; maximum ${c.limits.maxArea}.`,
            'limits',
          );
      }
    }
    if (
      (c.inputPositions && c.inputPositions.length !== c.inputs.length) ||
      (c.outputPositions && c.outputPositions.length !== c.outputs.length)
    )
      issue('Specify a position or null for every named input and output.');
    for (const edge of [
      ...(c.starterConnections ?? []),
      ...(c.starterDisconnected ?? []),
    ])
      if (
        Math.abs(edge.a.x - edge.b.x) + Math.abs(edge.a.y - edge.b.y) !== 1 ||
        [edge.a, edge.b].some((p) => !starter.some((t) => t.x === p.x && t.y === p.y))
      )
        issue('Starter connections must join adjacent starter tiles.');
    if (new Set(starter.map((t) => `${t.x},${t.y}`)).size !== starter.length)
      issue('Fixed tiles cannot overlap each other or the starter terminals.');
    for (const rule of c.limits.tiles)
      if (
        !playerOnly &&
        rule.type !== TileType.SingleInterface &&
        rule.available !== false &&
        rule.max !== undefined &&
        starter.filter((t) => t.type === rule.type).length > rule.max
      )
        issue(`The starter exceeds the ${rule.type} tile budget.`, ['limits', 'tiles']);
    if (
      c.buildOrigin &&
      starter.some(
        (t) =>
          t.x < c.buildOrigin!.x ||
          t.y < c.buildOrigin!.y ||
          t.x >= c.buildOrigin!.x + c.limits.width ||
          t.y >= c.buildOrigin!.y + c.limits.height,
      )
    )
      issue('The build area must contain every starter terminal and fixed tile.');
    if (new Set([...c.inputs, ...c.outputs]).size !== c.inputs.length + c.outputs.length)
      issue('Give every input and output a different name.');
    if (c.initialInputs && c.initialInputs.length !== c.inputs.length)
      issue('Specify an initial value for every input.', 'initialInputs');
    if (new Set(c.limits.tiles.map((t) => t.type)).size !== c.limits.tiles.length)
      issue('List each allowed tile type once.', ['limits', 'tiles']);
    const rows =
      c.tests.kind === 'truth-table'
        ? c.tests.rows
        : c.tests.scenarios.flatMap((s) => s.steps);
    if (
      rows.some(
        (r) =>
          r.inputs.length !== c.inputs.length || r.outputs.length !== c.outputs.length,
      )
    )
      issue('Every test must specify all inputs and outputs.', 'tests');
    if (c.tests.kind === 'truth-table') {
      if (c.tests.version !== 2 && c.inputs.length > MAX_TRUTH_TABLE_INPUTS)
        issue(
          `Use selective truth-table cases for more than ${MAX_TRUTH_TABLE_INPUTS} inputs.`,
          'tests',
        );
      const keys = c.tests.rows.map((r) => r.inputs.map(Number).join(''));
      if (new Set(keys).size !== c.tests.rows.length)
        issue('Include each input combination at most once.', 'tests');
      if (c.tests.version !== 2) {
        if (c.tests.rows.length !== 2 ** c.inputs.length)
          issue('Include each input combination exactly once.', 'tests');
        if (c.tests.rows.some((row) => row.outputs.includes(null)))
          issue('Ignored outputs require truth-table expectations version 2.', 'tests');
      } else if (!c.tests.rows.some((row) => row.outputs.some((bit) => bit !== null)))
        issue(
          'Assert at least one output as 0 or 1 in an included truth-table row.',
          'tests',
        );
      if (
        c.limits.feedback ||
        c.fixedTiles?.some((t) => t.type === TileType.Delay) ||
        c.limits.tiles.some((t) => t.type === TileType.Delay && t.available !== false) ||
        c.chipLibrary?.some((r) =>
          r.chip.logicCircuitDto.tileManagerDto.tiles.some(
            (t) => t.tileType === TileType.Delay,
          ),
        )
      )
        issue(
          'Truth tables require combinational circuits: remove Delay and gate feedback.',
          'tests',
        );
    } else {
      if (c.tests.version === undefined && rows.some((row) => row.outputs.includes(null)))
        issue('Ignored outputs require timed-sequence expectations version 2.', 'tests');
      if (!rows.some((row) => row.outputs.some((bit) => bit !== null)))
        issue('Assert at least one output as 0 or 1 in a timed sequence.', 'tests');
      if (new Set(c.tests.scenarios.map((s) => s.name)).size !== c.tests.scenarios.length)
        issue('Give each scenario a different name.', 'tests');
      if (c.tests.version === 3) {
        if (!c.tests.advance) issue('Choose how sequence steps advance.', 'tests');
        if (c.initialInputs !== undefined)
          issue('Use the first sequence row for starting inputs.', 'initialInputs');
        for (const scenario of c.tests.scenarios) {
          if (scenario.steps.some((row) => row.atMs !== undefined))
            issue('Sequence steps do not use absolute timestamps.', 'tests');
          if (
            c.tests.advance === 'duration' &&
            scenario.steps.some((row) => row.waitMs === undefined)
          )
            issue('Set the wait for each step.', 'tests');
          if (
            c.tests.advance === 'duration' &&
            scenario.steps.reduce((sum, row) => sum + (row.waitMs ?? 0), 0) > 60000
          )
            issue('A scenario can wait at most 60,000 ms in total.', 'tests');
        }
      } else {
        if (
          c.tests.advance !== undefined ||
          c.tests.scenarios.some((s) => s.steps.some((r) => r.waitMs !== undefined))
        )
          issue('Step durations require sequence version 3.', 'tests');
        for (const scenario of c.tests.scenarios)
          if (
            scenario.steps.some(
              (row, i) =>
                row.atMs === undefined ||
                (i > 0 && row.atMs <= (scenario.steps[i - 1].atMs ?? -1)),
            )
          )
            issue(
              `Times in ${scenario.name} must increase; combine simultaneous inputs in one step.`,
              'tests',
            );
      }
    }
  });
export type Challenge = z.infer<typeof ChallengeSchema>;
/** Recover incomplete author edits without weakening publish/import/run validation. */
export const DraftTestsSchema = z.discriminatedUnion('kind', [
  truthTable.extend({ rows: z.array(truthVector).max(MAX_TRUTH_TABLE_ROWS) }),
  ChallengeSchema.shape.tests.options[1],
]);
export type TestVector = { inputs: boolean[]; outputs: boolean[] };

export function terminalPosition(
  c: {
    inputPositions?: Array<z.infer<typeof TerminalPlacementSchema> | null>;
    outputPositions?: Array<z.infer<typeof TerminalPlacementSchema> | null>;
  },
  kind: 'inputs' | 'outputs',
  i: number,
): z.infer<typeof TerminalPlacementSchema> | null {
  const positions = kind === 'inputs' ? c.inputPositions : c.outputPositions;
  if (positions?.[i] === null) return null;
  return (
    positions?.[i] ?? {
      x: kind === 'inputs' ? 0 : 4,
      y: i * 2,
      rotation: 0,
    }
  );
}

export function truthRows(inputs: number, outputs: number): TestVector[] {
  if (!Number.isInteger(inputs) || inputs < 0 || inputs > MAX_TRUTH_TABLE_INPUTS)
    throw new RangeError(
      `Truth tables support up to ${MAX_TRUTH_TABLE_INPUTS} inputs; use scenarios for larger layouts.`,
    );
  return Array.from({ length: 2 ** inputs }, (_, n) => ({
    inputs: Array.from({ length: inputs }, (_, i) => !!(n & (1 << (inputs - i - 1)))),
    outputs: Array(outputs).fill(false),
  }));
}

/** Explicit null means player-placed. Omitted legacy arrays retain their fixed defaults. */
export function hasPlaceableInterfaces(
  c: Pick<Challenge, 'inputPositions' | 'outputPositions'>,
) {
  return !!(c.inputPositions?.includes(null) || c.outputPositions?.includes(null));
}
export function isPlaceableInterface(c: Challenge, label: string) {
  return (['inputs', 'outputs'] as const).some((kind) => {
    const index = c[kind].indexOf(label);
    return index >= 0 && terminalPosition(c, kind, index) === null;
  });
}
